import { isEmpty } from "es-toolkit/compat";
import { atom } from "jotai";

import type { HttpRequestPage } from "@moment/api-collab/api-types";
import { Logger } from "@moment/logging";

import type { DocsListLocalEntry } from "../document-list/document-list.types";
import { fs } from "../filesystem/filesystem.atom";
import { getPageDirPath } from "../paths/paths.internal";
import type { PageRefProps } from "../reboot/refs";
import { $requestPageCollabConnection } from "../reboot/request-page-collab-connection.atom";
import {
	getLatestRequestPage,
	makeRequestPageRemoteChangesCommand,
	makeRequestPageSendToCollabCommand,
} from "../reboot/utils/request-page-utils";
import { HttpRequestLoadError } from "./errors";
import { $loadHttpRequestPage, $setHttpRequestPageError } from "./request-page.atom";

const logger = new Logger("load-http-request-from-reboot");
const fsApi = fs();

export interface LoadHttpRequestFromRebootInput extends PageRefProps {
	key: string;
	onPersist?: (request: HttpRequestPage) => Promise<void>;
	localEntry?: DocsListLocalEntry;
	pagePath?: string;
}

/**
 * Loads an HTTP request page from the Reboot collab server.
 */
export const $loadHttpRequestFromReboot = atom(
	null,
	async (get, set, input: LoadHttpRequestFromRebootInput) => {
		const { key, localEntry, pagePath, ...props } = input;
		const { documentId, pageId } = props;

		try {
			// Get the collab connection
			const connection = await get($requestPageCollabConnection(props));
			if (!connection) {
				throw new Error("Reboot connection not available");
			}

			const { rebootStoreRef, store, context } = connection;

			// Read the initial request page directly from the store's state.
			// The store is already populated with data from queryEvaluatorStateFromReboot.
			// We avoid using store.from("request").subscribe() because subscribeQuery
			// silently drops empty results, which would cause the Promise to hang forever
			// if the table has no data (e.g., due to permissions returning empty tuples).
			const initialRequest = getLatestRequestPage(store);
			if (!initialRequest) {
				logger.error("No request page data returned from Reboot", props);
				throw new Error("No request page data returned");
			}

			// Create the collab callbacks from the store
			const sendToCollab = makeRequestPageSendToCollabCommand(store);

			// Use the store's current epoch as the initial version for the remoteChanges stream.
			// The remoteChanges command subtracts 1 internally, so we pass the epoch directly.
			// Worst case scenario, we use fromVersion = 0. In this case the Reboot server will
			// send us all the updates it knows about, which may be quite a bit of data, which
			// should be fine.
			const initialVersion = store.latestEpoch;
			const remoteChanges = makeRequestPageRemoteChangesCommand(
				rebootStoreRef,
				context,
				initialVersion,
				store
			);

			// Create onPersist callback if we have a local entry
			let onPersist: ((request: HttpRequestPage) => Promise<void>) | undefined = undefined;
			if (!isEmpty(localEntry) && localEntry.path) {
				const pageDirPath = await getPageDirPath(localEntry.path, pageId, pagePath);
				const pageFilePath = await get(fsApi.pathJoin([pageDirPath, "index.yml"]));

				// Immediately persist the initial Reboot content to disk. When the owner
				// reconnects to collab after being offline, pages may have been added or
				// modified by collaborators and won't exist on disk yet. Write them now so
				// the local files catch up without requiring the user to edit each page.
				await set(fsApi.mkdir, { dirPath: pageDirPath, options: { recursive: true } });
				await set(fsApi.writeYamlFile, {
					filePath: pageFilePath,
					content: initialRequest,
					documentId,
				});
				logger.info("Persisted initial Reboot request page to disk", {
					documentId,
					pageId,
					pageFilePath,
				});

				onPersist = async (request: HttpRequestPage): Promise<void> => {
					await set(fsApi.writeYamlFile, {
						filePath: pageFilePath,
						content: request,
						documentId,
					});
				};
			} else if (input.onPersist) {
				onPersist = input.onPersist;
			}

			logger.info("Loading HTTP request page from Reboot", {
				key,
				...props,
			});

			// Load the HTTP request page. NEVER SSR, obviously, as this is from Reboot.
			set($loadHttpRequestPage, {
				key,
				record: {
					source: "remote",
					request: initialRequest,
					importEpoch: 0,
				},
				ssr: false,
				sendToCollab,
				remoteChanges,
				onPersist,
				connection,
			});
		} catch (err) {
			const error = new HttpRequestLoadError(err);
			logger.error("Failed to load HTTP request page from Reboot", { key, err });
			set($setHttpRequestPageError, { key, error });
		}
	}
);
$loadHttpRequestFromReboot.debugLabel = "loadHttpRequestFromReboot";
